// VendTrade landing / browse page. Public — anyone can view; posting
// requires login. Filters by direction (offer / looking_for), category,
// and free-text search.
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Search, Handshake, ArrowLeftRight, MapPin, Sparkles } from "lucide-react";
import { CATEGORIES, CategoryChip, DirectionPill, DIR_MAP } from "./shared";

export default function VendTradeBrowse() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [direction, setDirection] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [qActive, setQActive] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 60 };
      if (direction) params.direction = direction;
      if (category) params.category = category;
      if (qActive) params.q = qActive;
      const { data } = await api.get("/vendtrade/listings", { params });
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [direction, category, qActive]);

  useEffect(() => { load(); }, [load]);

  const offerCount = useMemo(
    () => items.filter((i) => i.direction === "offer").length,
    [items],
  );
  const seekCount = items.length - offerCount;

  return (
    <div className="mx-auto max-w-6xl px-6 py-14" data-testid="vendtrade-browse-page">
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-4"
          style={{ background: "rgba(196,79,54,0.10)", color: "var(--terracotta)" }}
        >
          <Sparkles size={12} /> VendTrade
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-tight">
          Trade what you have. Get what you need.
        </h1>
        <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "var(--ink-soft)" }}>
          Skip the cash. Barter goods, services, resources, or promotion with
          the AddVendTour community — matched by category, city, and what
          you&apos;re actually looking for.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button
            onClick={() => nav(user ? "/vendtrade/new" : "/login?next=/vendtrade/new")}
            data-testid="vendtrade-cta-post"
            className="btn-primary py-6 px-7 rounded-full inline-flex items-center gap-2"
          >
            <Plus size={16} /> Post a trade
          </Button>
          {user && (
            <Link
              to="/vendtrade/mine"
              data-testid="vendtrade-cta-mine"
              className="text-sm underline"
              style={{ color: "var(--ink-soft)" }}
            >
              My listings &amp; matches &rarr;
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-14 flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <FilterPill
            active={direction === ""}
            onClick={() => setDirection("")}
            label={`All (${items.length})`}
            testId="vendtrade-filter-all"
          />
          <FilterPill
            active={direction === "offer"}
            onClick={() => setDirection("offer")}
            label={`Offers (${offerCount})`}
            icon={Handshake}
            testId="vendtrade-filter-offer"
          />
          <FilterPill
            active={direction === "looking_for"}
            onClick={() => setDirection("looking_for")}
            label={`Looking for (${seekCount})`}
            icon={ArrowLeftRight}
            testId="vendtrade-filter-looking"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterPill
            active={category === ""}
            onClick={() => setCategory("")}
            label="All categories"
            testId="vendtrade-filter-cat-all"
          />
          {CATEGORIES.map((c) => (
            <FilterPill
              key={c.value}
              active={category === c.value}
              onClick={() => setCategory(c.value)}
              label={c.label}
              icon={c.icon}
              testId={`vendtrade-filter-cat-${c.value}`}
            />
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setQActive(q); }}
          className="flex gap-2"
          data-testid="vendtrade-search-form"
        >
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute top-1/2 -translate-y-1/2 left-3 opacity-50"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by keyword (e.g. logo, kombucha, booth, printing)"
              className="pl-9"
              data-testid="vendtrade-search-input"
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            className="rounded-full px-6"
            data-testid="vendtrade-search-submit"
          >
            Search
          </Button>
          {qActive && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setQ(""); setQActive(""); }}
              data-testid="vendtrade-search-clear"
              className="rounded-full"
            >
              Clear
            </Button>
          )}
        </form>
      </div>

      {/* Listings */}
      <div className="mt-10">
        {error && (
          <div
            className="rounded-md p-3 text-sm"
            style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}
          >
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-16 text-center text-sm" style={{ color: "var(--ink-soft)" }}>
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="vendtrade-empty-state"
            className="py-16 text-center rounded-lg border"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <div className="font-serif text-2xl">Nothing here yet.</div>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              Be the first to list a trade in this filter.
            </p>
            <Button
              onClick={() => nav(user ? "/vendtrade/new" : "/login?next=/vendtrade/new")}
              className="btn-primary mt-6 py-5 px-7 rounded-full inline-flex items-center gap-2"
            >
              <Plus size={16} /> Post the first one
            </Button>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="vendtrade-listing-grid"
          >
            {items.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, icon: Icon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition"
      style={{
        background: active ? "var(--ink)" : "rgba(0,0,0,0.05)",
        color: active ? "#fff" : "var(--ink)",
      }}
    >
      {Icon && <Icon size={12} />}
      {label}
    </button>
  );
}

function ListingCard({ listing }) {
  return (
    <Link
      to={`/vendtrade/listings/${listing.id}`}
      data-testid={`vendtrade-card-${listing.id}`}
      className="block rounded-lg border overflow-hidden hover:shadow-md transition"
      style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
    >
      {listing.image_url && (
        <div
          className="w-full aspect-[4/3] overflow-hidden"
          data-testid={`vendtrade-card-image-${listing.id}`}
          style={{ background: "rgba(0,0,0,0.04)" }}
        >
          <img
            src={listing.image_url}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <DirectionPill value={listing.direction} />
          <CategoryChip value={listing.category} />
        </div>
        <h3 className="mt-3 font-serif text-xl leading-tight line-clamp-2">
          {listing.title}
        </h3>
        <p
          className="mt-2 text-sm line-clamp-3"
          style={{ color: "var(--ink-soft)" }}
        >
          {listing.description}
        </p>
        <div
          className="mt-4 pt-3 border-t flex items-center justify-between text-xs"
          style={{ borderColor: "rgba(0,0,0,0.08)", color: "var(--ink-soft)" }}
        >
          <span className="inline-flex items-center gap-1">
            <MapPin size={12} /> {listing.city}
          </span>
          <span className="truncate max-w-[150px]" title={listing.owner?.business_name || listing.owner?.name}>
            {listing.owner?.business_name || listing.owner?.name || "A trader"}
          </span>
        </div>
      </div>
    </Link>
  );
}
